import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SelectUI from '../../components/ui/Select';
import { cn } from '../../utils/cn';

// ─── Constants ────────────────────────────────────────────────────────────────

const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const DAYS_FULL = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const TIME_SLOTS = [
  '07:00', '07:30', '08:00', '08:30', '09:00', '09:30',
  '10:00', '10:30', '11:00', '11:30', '12:00', '12:30',
  '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00', '17:30', '18:00', '18:30',
  '19:00', '19:30', '20:00',
];

const YEARS_EXP_OPTIONS = [
  { value: '0-1', label: "Moins d\'1 an" },
  { value: '1-3', label: '1 à 3 ans' },
  { value: '3-5', label: '3 à 5 ans' },
  { value: '5-10', label: '5 à 10 ans' },
  { value: '10+', label: 'Plus de 10 ans' },
];

const SERVICE_CATEGORIES = [
  { id: 'agent_immobilier', label: 'Agent Immobilier', icon: 'Home', desc: 'Vente, location et gestion de biens', color: 'from-amber-500 to-orange-500' },
  { id: 'notaire', label: 'Notaire', icon: 'Scale', desc: 'Actes notariés et transactions légales', color: 'from-blue-600 to-indigo-600' },
  { id: 'architecte', label: 'Architecte', icon: 'PenTool', desc: 'Conception et plans de construction', color: 'from-teal-500 to-cyan-600' },
  { id: 'geometre', label: 'Géomètre', icon: 'Map', desc: 'Bornage et relevés topographiques', color: 'from-green-500 to-emerald-600' },
  { id: 'promoteur', label: 'Promoteur', icon: 'Building2', desc: 'Développement et promotion immobilière', color: 'from-purple-500 to-violet-600' },
  { id: 'expert_evaluation', label: 'Expert en Évaluation', icon: 'BarChart2', desc: 'Estimation et expertise de biens', color: 'from-rose-500 to-pink-600' },
  { id: 'gestionnaire', label: 'Gestionnaire de Biens', icon: 'Briefcase', desc: 'Administration et gestion locative', color: 'from-slate-500 to-gray-600' },
  { id: 'conseiller_financier', label: 'Conseiller Financier', icon: 'TrendingUp', desc: 'Financement et investissement immobilier', color: 'from-yellow-500 to-amber-600' },
];

const COMMISSION_TIERS = [
  {
    rate: 3,
    label: 'Essentiel',
    desc: 'Idéal pour démarrer, accès aux fonctionnalités de base',
    perks: ['Profil vérifié', 'Accès aux demandes clients', 'Support standard'],
    highlight: false,
  },
  {
    rate: 5,
    label: 'Professionnel',
    desc: 'Le choix le plus populaire parmi nos prestataires',
    perks: ['Tout Essentiel', 'Mise en avant dans les résultats', 'Statistiques avancées', 'Support prioritaire'],
    highlight: true,
  },
  {
    rate: 8,
    label: 'Premium',
    desc: 'Visibilité maximale et outils exclusifs',
    perks: ['Tout Professionnel', 'Badge Premium', 'Accès aux clients VIP', 'Gestionnaire dédié', 'Rapports personnalisés'],
    highlight: false,
  },
];

const STEPS = [
  { id: 'profile', shortLabel: 'Profil', icon: 'User' },
  { id: 'services', shortLabel: 'Services', icon: 'Briefcase' },
  { id: 'disponibilites', shortLabel: 'Horaires', icon: 'Calendar' },
  { id: 'commission', shortLabel: 'Commission', icon: 'Percent' },
];

// ─── Shadcn-style Stepper ─────────────────────────────────────────────────────

const Stepper = ({ currentStep, steps }) => (
  <nav aria-label="Étapes d'onboarding" className="mb-8">
    <ol className="flex items-center">
      {steps?.map((step, idx) => {
        const stepNum = idx + 1;
        const isCompleted = stepNum < currentStep;
        const isActive = stepNum === currentStep;
        const isLast = idx === steps?.length - 1;

        return (
          <React.Fragment key={step?.id}>
            <li className="flex flex-col items-center relative">
              {/* Circle */}
              <div
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-full border-2 text-sm font-medium transition-all duration-300 z-10',
                  isCompleted && 'border-primary bg-primary text-primary-foreground',
                  isActive && 'border-primary bg-background text-primary shadow-sm ring-4 ring-primary/10',
                  !isCompleted && !isActive && 'border-border bg-background text-muted-foreground'
                )}
              >
                {isCompleted ? (
                  <Icon name="Check" size={15} />
                ) : (
                  <Icon name={step?.icon} size={14} />
                )}
              </div>
              {/* Label */}
              <span
                className={cn(
                  'mt-2 text-[10px] font-medium tracking-wide uppercase whitespace-nowrap hidden sm:block',
                  isActive ? 'text-primary' : isCompleted ? 'text-primary/70' : 'text-muted-foreground'
                )}
              >
                {step?.shortLabel}
              </span>
            </li>
            {/* Connector line */}
            {!isLast && (
              <div className="flex-1 mx-2 mt-[-18px] sm:mt-[-22px]">
                <div
                  className={cn(
                    'h-0.5 w-full transition-all duration-500',
                    stepNum < currentStep ? 'bg-primary' : 'bg-border'
                  )}
                />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </ol>
  </nav>
);

// ─── Field wrapper for consistent layout ─────────────────────────────────────

const FormField = ({ label, required = false, error, description = '', children, className = '' }) => (
  <div className={cn('space-y-1.5', className)}>
    {label && (
      <label className={cn('text-sm font-medium leading-none', error ? 'text-destructive' : 'text-foreground')}>
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </label>
    )}
    {children}
    {description && !error && <p className="text-xs text-muted-foreground">{description}</p>}
    {error && (
      <p className="flex items-center gap-1 text-xs text-destructive">
        <Icon name="AlertCircle" size={11} />
        {error}
      </p>
    )}
  </div>
);

// ─── Step 1: Profile ──────────────────────────────────────────────────────────

const StepProfile = ({ data, onChange, errors }) => {
  const set = (field, value) => onChange({ ...data, [field]: value });

  return (
    <div className="space-y-5">
      {/* Section header */}
      <div className="flex items-center gap-3 pb-4 border-b border-border">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
          <Icon name="User" size={18} className="text-primary" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-foreground">Complétez votre profil</h2>
          <p className="text-sm text-muted-foreground">Ces informations seront visibles par vos futurs clients</p>
        </div>
      </div>

      {/* Avatar upload */}
      <div className="flex items-center gap-4 p-4 rounded-lg border border-border bg-muted/30">
        <div className="h-14 w-14 rounded-lg border-2 border-dashed border-border bg-background flex items-center justify-center flex-shrink-0 overflow-hidden">
          {data?.avatarPreview ? (
            <img src={data?.avatarPreview} alt="Avatar" className="h-full w-full object-cover" />
          ) : (
            <Icon name="Camera" size={20} className="text-muted-foreground" />
          )}
        </div>
        <div>
          <p className="text-sm font-medium text-foreground">Photo de profil</p>
          <p className="text-xs text-muted-foreground mt-0.5">JPG ou PNG, max 5 Mo</p>
          <label className="mt-2 inline-flex items-center gap-1.5 text-xs text-primary font-medium cursor-pointer hover:text-primary/80 transition-colors">
            <Icon name="Upload" size={12} />
            <span>Choisir une photo</span>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e?.target?.files?.[0];
                if (file) {
                  const url = URL.createObjectURL(file);
                  onChange({ ...data, avatarFile: file, avatarPreview: url });
                }
              }}
            />
          </label>
        </div>
      </div>

      {/* Name row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="Prénom"
          required
          value={data?.firstName || ''}
          onChange={(e) => set('firstName', e?.target?.value)}
          placeholder="Kouassi"
          error={errors?.firstName}
        />
        <Input
          label="Nom"
          required
          value={data?.lastName || ''}
          onChange={(e) => set('lastName', e?.target?.value)}
          placeholder="Atta"
          error={errors?.lastName}
        />
      </div>

      {/* Phone */}
      <FormField label="Numéro de téléphone / WhatsApp" required error={errors?.phone}>
        <div className="flex">
          <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground select-none">
            🇨🇮 +225
          </span>
          <input
            type="tel"
            value={data?.phone || ''}
            onChange={(e) => set('phone', e?.target?.value)}
            placeholder="07 00 00 00 00"
            className={cn(
              'flex h-9 flex-1 rounded-r-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors',
              errors?.phone && 'border-destructive focus-visible:ring-destructive'
            )}
          />
        </div>
      </FormField>

      {/* City */}
      <Input
        label="Ville / Commune"
        required
        value={data?.city || ''}
        onChange={(e) => set('city', e?.target?.value)}
        placeholder="Abidjan, Cocody"
        error={errors?.city}
      />

      {/* Years experience */}
      <SelectUI
        label="Années d'expérience"
        options={YEARS_EXP_OPTIONS}
        value={data?.yearsExp || ''}
        onChange={(val) => set('yearsExp', val)}
        placeholder="Sélectionner..."
        error={errors?.yearsExp}
      />

      {/* Bio */}
      <FormField
        label="Biographie professionnelle"
        description="Décrivez votre expertise, vos spécialités et ce qui vous distingue"
        error={errors?.bio}
      >
        <textarea
          value={data?.bio || ''}
          onChange={(e) => set('bio', e?.target?.value)}
          placeholder="Ex: Spécialiste en immobilier résidentiel depuis 8 ans, je vous accompagne..."
          rows={4}
          maxLength={500}
          className={cn(
            'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors resize-none',
            errors?.bio && 'border-destructive focus-visible:ring-destructive'
          )}
        />
        <p className="text-xs text-muted-foreground text-right">{(data?.bio || '')?.length}/500</p>
      </FormField>
    </div>
  );
};

// ─── Step 2: Services ─────────────────────────────────────────────────────────

const StepServices = ({ data, onChange, errors }) => {
  const toggleService = (id) => {
    const current = data?.services || [];
    const updated = current?.includes(id) ? current?.filter((s) => s !== id) : [...current, id];
    onChange({ ...data, services: updated });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 pb-4 border-b border-border">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
          <Icon name="Briefcase" size={18} className="text-blue-600" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-foreground">Vos services</h2>
          <p className="text-sm text-muted-foreground">Sélectionnez les services que vous proposez</p>
        </div>
      </div>

      {errors?.services && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-destructive/30 bg-destructive/5 text-sm text-destructive">
          <Icon name="AlertCircle" size={14} />
          {errors?.services}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {SERVICE_CATEGORIES?.map((svc) => {
          const isSelected = (data?.services || [])?.includes(svc?.id);
          return (
            <button
              key={svc?.id}
              type="button"
              onClick={() => toggleService(svc?.id)}
              className={cn(
                'relative flex items-start gap-3 p-3.5 rounded-lg border-2 text-left transition-all duration-150',
                isSelected
                  ? 'border-primary bg-primary/5' :'border-border bg-background hover:border-primary/40 hover:bg-accent/30'
              )}
            >
              <div className={cn('flex h-9 w-9 items-center justify-center rounded-md bg-gradient-to-br flex-shrink-0', svc?.color)}>
                <Icon name={svc?.icon} size={16} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={cn('text-sm font-medium', isSelected ? 'text-primary' : 'text-foreground')}>
                  {svc?.label}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{svc?.desc}</p>
              </div>
              {isSelected && (
                <div className="absolute top-2.5 right-2.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary">
                  <Icon name="Check" size={10} className="text-primary-foreground" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {(data?.services || [])?.length > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-green-200 bg-green-50 text-sm text-green-700">
          <Icon name="CheckCircle" size={14} className="text-green-600 flex-shrink-0" />
          {(data?.services || [])?.length} service{(data?.services || [])?.length > 1 ? 's' : ''} sélectionné{(data?.services || [])?.length > 1 ? 's' : ''}
        </div>
      )}

      <Input
        label="Spécialité principale"
        value={data?.specialty || ''}
        onChange={(e) => onChange({ ...data, specialty: e?.target?.value })}
        placeholder="Ex: Villas de luxe, Immobilier commercial..."
        description="Optionnel — précisez votre domaine d'expertise"
      />
    </div>
  );
};

// ─── Step 3: Availability ─────────────────────────────────────────────────────

const StepAvailability = ({ data, onChange, errors }) => {
  const availability = data?.availability || {};
  const [expandedDay, setExpandedDay] = useState(null);

  const toggleDay = (dayIdx) => {
    const key = `day_${dayIdx}`;
    const current = availability?.[key] || { enabled: false, slots: [] };
    onChange({
      ...data,
      availability: {
        ...availability,
        [key]: {
          ...current,
          enabled: !current?.enabled,
          slots: current?.enabled ? [] : ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'],
        },
      },
    });
  };

  const toggleSlot = (dayIdx, slot) => {
    const key = `day_${dayIdx}`;
    const current = availability?.[key] || { enabled: true, slots: [] };
    const slots = current?.slots || [];
    const updated = slots?.includes(slot) ? slots?.filter((s) => s !== slot) : [...slots, slot]?.sort();
    onChange({ ...data, availability: { ...availability, [key]: { ...current, slots: updated } } });
  };

  const enabledDays = Object.values(availability)?.filter((d) => d?.enabled)?.length;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 pb-4 border-b border-border">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-500/10">
          <Icon name="Calendar" size={18} className="text-teal-600" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-foreground">Vos disponibilités</h2>
          <p className="text-sm text-muted-foreground">Définissez vos jours et créneaux horaires</p>
        </div>
      </div>

      {errors?.availability && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-destructive/30 bg-destructive/5 text-sm text-destructive">
          <Icon name="AlertCircle" size={14} />
          {errors?.availability}
        </div>
      )}

      {/* Quick presets */}
      <div className="flex flex-wrap gap-2">
        <span className="text-xs text-muted-foreground self-center mr-1">Raccourcis :</span>
        {[
          { label: 'Semaine', days: [0, 1, 2, 3, 4] },
          { label: 'Lun–Sam', days: [0, 1, 2, 3, 4, 5] },
          { label: 'Week-end', days: [5, 6] },
        ]?.map((preset) => (
          <button
            key={preset?.label}
            type="button"
            onClick={() => {
              const newAvail = { ...availability };
              preset?.days?.forEach((d) => {
                newAvail[`day_${d}`] = { enabled: true, slots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'] };
              });
              onChange({ ...data, availability: newAvail });
            }}
            className="inline-flex items-center h-7 px-2.5 rounded-md border border-input bg-background text-xs font-medium text-foreground hover:bg-accent hover:border-primary/40 transition-colors"
          >
            {preset?.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onChange({ ...data, availability: {} })}
          className="inline-flex items-center h-7 px-2.5 rounded-md border border-input bg-background text-xs font-medium text-muted-foreground hover:bg-destructive/5 hover:border-destructive/40 hover:text-destructive transition-colors"
        >
          Réinitialiser
        </button>
      </div>

      {/* Day rows */}
      <div className="space-y-2">
        {DAYS_FR?.map((_, idx) => {
          const key = `day_${idx}`;
          const dayData = availability?.[key] || { enabled: false, slots: [] };
          const isEnabled = dayData?.enabled;
          const isExpanded = expandedDay === idx;

          return (
            <div
              key={idx}
              className={cn(
                'rounded-lg border transition-all duration-200 overflow-hidden',
                isEnabled ? 'border-primary/30 bg-primary/3' : 'border-border bg-background'
              )}
            >
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  {/* Toggle switch */}
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isEnabled}
                    onClick={() => toggleDay(idx)}
                    className={cn(
                      'relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring flex-shrink-0',
                      isEnabled ? 'bg-primary' : 'bg-input'
                    )}
                  >
                    <span
                      className={cn(
                        'inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200',
                        isEnabled ? 'translate-x-4' : 'translate-x-0.5'
                      )}
                    />
                  </button>
                  <span className={cn('text-sm font-medium', isEnabled ? 'text-foreground' : 'text-muted-foreground')}>
                    {DAYS_FULL?.[idx]}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {isEnabled && (
                    <span className="text-xs text-primary font-medium">
                      {dayData?.slots?.length || 0} créneaux
                    </span>
                  )}
                  {isEnabled && (
                    <button
                      type="button"
                      onClick={() => setExpandedDay(isExpanded ? null : idx)}
                      className="flex h-6 w-6 items-center justify-center rounded-md border border-input bg-background hover:bg-accent transition-colors"
                    >
                      <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={12} className="text-muted-foreground" />
                    </button>
                  )}
                </div>
              </div>

              {isEnabled && isExpanded && (
                <div className="px-4 pb-4 border-t border-border/50">
                  <p className="text-xs text-muted-foreground mb-3 mt-3">Sélectionnez vos créneaux disponibles :</p>
                  <div className="flex flex-wrap gap-1.5">
                    {TIME_SLOTS?.map((slot) => {
                      const isSlotActive = (dayData?.slots || [])?.includes(slot);
                      return (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => toggleSlot(idx, slot)}
                          className={cn(
                            'inline-flex items-center h-7 px-2.5 rounded-md text-xs font-medium transition-colors',
                            isSlotActive
                              ? 'bg-primary text-primary-foreground'
                              : 'border border-input bg-background text-foreground hover:bg-accent hover:border-primary/40'
                          )}
                        >
                          {slot}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {enabledDays > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-green-200 bg-green-50 text-sm text-green-700">
          <Icon name="CheckCircle" size={14} className="text-green-600 flex-shrink-0" />
          {enabledDays} jour{enabledDays > 1 ? 's' : ''} de disponibilité configuré{enabledDays > 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

// ─── Step 4: Commission ───────────────────────────────────────────────────────

const StepCommission = ({ data, onChange, errors }) => (
  <div className="space-y-5">
    <div className="flex items-center gap-3 pb-4 border-b border-border">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/10">
        <Icon name="Percent" size={18} className="text-amber-600" />
      </div>
      <div>
        <h2 className="text-base font-semibold text-foreground">Taux de commission</h2>
        <p className="text-sm text-muted-foreground">Choisissez le plan qui correspond à vos ambitions</p>
      </div>
    </div>

    <div className="space-y-2.5">
      {COMMISSION_TIERS?.map((tier) => {
        const isSelected = data?.commissionRate === tier?.rate;
        return (
          <button
            key={tier?.rate}
            type="button"
            onClick={() => onChange({ ...data, commissionRate: tier?.rate })}
            className={cn(
              'relative w-full text-left p-4 rounded-lg border-2 transition-all duration-150',
              isSelected ? 'border-primary bg-primary/5' : 'border-border bg-background hover:border-primary/40 hover:bg-accent/20',
              tier?.highlight && !isSelected ? 'border-amber-300/60 bg-amber-50/40' : ''
            )}
          >
            {tier?.highlight && (
              <span className="absolute -top-2.5 left-4 inline-flex items-center px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-semibold uppercase tracking-wide">
                Recommandé
              </span>
            )}
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-foreground">{tier?.label}</span>
                  <span className={cn('text-sm font-semibold', isSelected ? 'text-primary' : 'text-muted-foreground')}>
                    — {tier?.rate}%
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mb-2.5">{tier?.desc}</p>
                <ul className="space-y-1">
                  {tier?.perks?.map((perk) => (
                    <li key={perk} className="flex items-center gap-1.5">
                      <Icon name="Check" size={11} className={isSelected ? 'text-primary' : 'text-muted-foreground'} />
                      <span className="text-xs text-muted-foreground">{perk}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Radio indicator */}
              <div
                className={cn(
                  'mt-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 flex-shrink-0 transition-all',
                  isSelected ? 'border-primary bg-primary' : 'border-input bg-background'
                )}
              >
                {isSelected && <div className="h-2 w-2 rounded-full bg-primary-foreground" />}
              </div>
            </div>
          </button>
        );
      })}
    </div>

    {/* Summary + terms */}
    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Icon name="FileText" size={14} className="text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">Récapitulatif de l'accord</span>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed">
        En confirmant, vous acceptez que <strong className="text-foreground">Kapital Estates</strong> perçoive une commission de{' '}
        <strong className="text-primary">{data?.commissionRate || 5}%</strong> sur chaque transaction réalisée via la plateforme.
        Ce taux peut être renégocié après 6 mois d'activité.
      </p>
      <div className="flex items-start gap-2.5 pt-1">
        <input
          type="checkbox"
          id="accept-terms"
          checked={data?.acceptedTerms || false}
          onChange={(e) => onChange({ ...data, acceptedTerms: e?.target?.checked })}
          className="mt-0.5 h-4 w-4 rounded border-input text-primary focus:ring-primary/30 cursor-pointer"
        />
        <label htmlFor="accept-terms" className="text-sm text-muted-foreground cursor-pointer leading-relaxed">
          J'ai lu et j'accepte les{' '}
          <span className="text-primary underline underline-offset-2">conditions générales</span> et la{' '}
          <span className="text-primary underline underline-offset-2">politique de commission</span> de Kapital Estates.
        </label>
      </div>
      {errors?.acceptedTerms && (
        <p className="flex items-center gap-1 text-xs text-destructive">
          <Icon name="AlertCircle" size={11} />
          {errors?.acceptedTerms}
        </p>
      )}
    </div>
  </div>
);

// ─── Welcome Banner ───────────────────────────────────────────────────────────

const WelcomeBanner = ({ name, onStart }) => (
  <div className="min-h-screen bg-gradient-to-br from-brand-earth via-brand-earth/90 to-brand-navy flex items-center justify-center p-6 relative overflow-hidden">
    <div className="absolute top-0 right-0 w-96 h-96 bg-brand-gold/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
    <div className="absolute bottom-0 left-0 w-80 h-80 bg-white/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
    <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }} />

    <div className="relative z-10 max-w-lg w-full text-center">
      <div className="w-24 h-24 rounded-3xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center mx-auto mb-8 shadow-ios-heavy">
        <Icon name="Award" size={44} className="text-brand-gold" />
      </div>
      <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-brand-gold/20 border border-brand-gold/30 rounded-full mb-6">
        <div className="w-2 h-2 rounded-full bg-brand-gold animate-pulse" />
        <span className="text-brand-gold text-xs font-medium uppercase tracking-widest">Compte approuvé</span>
      </div>
      <h1 className="text-3xl sm:text-4xl font-display font-display-semibold text-white leading-tight mb-4">
        Bienvenue,<br />
        <span className="text-brand-gold">{name || 'Professionnel'} 🎉</span>
      </h1>
      <p className="text-white/70 text-base leading-relaxed mb-10 max-w-sm mx-auto">
        Votre candidature a été approuvée. Configurez votre espace professionnel en quelques étapes pour commencer à recevoir des clients.
      </p>
      <div className="grid grid-cols-2 gap-3 mb-10 text-left">
        {[
          { icon: 'User', label: 'Profil', desc: 'Vos informations' },
          { icon: 'Briefcase', label: 'Services', desc: 'Ce que vous proposez' },
          { icon: 'Calendar', label: 'Disponibilités', desc: 'Vos créneaux' },
          { icon: 'Percent', label: 'Commission', desc: 'Votre accord' },
        ]?.map((item, i) => (
          <div key={i} className="flex items-center gap-3 p-3 bg-white/8 rounded-xl border border-white/10">
            <div className="w-9 h-9 rounded-xl bg-brand-gold/20 flex items-center justify-center flex-shrink-0">
              <Icon name={item?.icon} size={16} className="text-brand-gold" />
            </div>
            <div>
              <p className="text-white text-sm font-medium">{item?.label}</p>
              <p className="text-white/50 text-xs">{item?.desc}</p>
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={onStart}
        className="w-full h-14 bg-brand-gold text-brand-earth rounded-xl font-semibold text-base shadow-ios-heavy hover:bg-brand-gold/90 active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2"
      >
        <span>Commencer la configuration</span>
        <Icon name="ArrowRight" size={20} />
      </button>
      <p className="text-white/40 text-xs mt-4">Environ 3 minutes · 4 étapes simples</p>
    </div>
  </div>
);

// ─── Success Screen ───────────────────────────────────────────────────────────

const SuccessScreen = ({ onGoToDashboard }) => (
  <div className="min-h-screen bg-gradient-to-br from-green-600 to-teal-700 flex items-center justify-center p-6 relative overflow-hidden">
    <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
    <div className="absolute bottom-0 left-0 w-80 h-80 bg-white/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
    <div className="relative z-10 max-w-md w-full text-center">
      <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-8 shadow-ios-heavy">
        <Icon name="CheckCircle" size={48} className="text-white" />
      </div>
      <h1 className="text-3xl font-display font-display-semibold text-white mb-4">Configuration terminée !</h1>
      <p className="text-white/80 text-base leading-relaxed mb-10">
        Votre espace professionnel est prêt. Vous pouvez maintenant recevoir des demandes de clients et gérer vos rendez-vous.
      </p>
      <button
        onClick={onGoToDashboard}
        className="w-full h-14 bg-white text-green-700 rounded-xl font-semibold text-base shadow-ios-heavy hover:bg-white/90 active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2"
      >
        <Icon name="LayoutDashboard" size={20} />
        <span>Accéder à mon tableau de bord</span>
      </button>
    </div>
  </div>
);

// ─── Validation ───────────────────────────────────────────────────────────────

const validateStep = (step, formData) => {
  const errs = {};
  if (step === 1) {
    if (!formData?.firstName?.trim()) errs.firstName = 'Le prénom est requis';
    if (!formData?.lastName?.trim()) errs.lastName = 'Le nom est requis';
    if (!formData?.phone?.trim()) errs.phone = 'Le numéro de téléphone est requis';
    if (!formData?.city?.trim()) errs.city = 'La ville est requise';
  }
  if (step === 2) {
    if (!(formData?.services || [])?.length) errs.services = 'Veuillez sélectionner au moins un service';
  }
  if (step === 3) {
    const enabled = Object.values(formData?.availability || {})?.filter((d) => d?.enabled);
    if (!enabled?.length) errs.availability = 'Veuillez configurer au moins un jour de disponibilité';
  }
  if (step === 4) {
    if (!formData?.acceptedTerms) errs.acceptedTerms = 'Vous devez accepter les conditions pour continuer';
  }
  return errs;
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ProfessionalOnboarding = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [phase, setPhase] = useState('welcome');
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [stepErrors, setStepErrors] = useState({});

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    city: '',
    yearsExp: '',
    bio: '',
    avatarFile: null,
    avatarPreview: null,
    services: [],
    specialty: '',
    availability: {},
    commissionRate: 5,
    acceptedTerms: false,
  });

  useEffect(() => {
    if (!user) return;
    const meta = user?.user_metadata || {};
    setFormData((prev) => ({
      ...prev,
      firstName: meta?.first_name || meta?.full_name?.split(' ')?.[0] || '',
      lastName: meta?.last_name || meta?.full_name?.split(' ')?.slice(1)?.join(' ') || '',
    }));
  }, [user]);

  const handleNext = () => {
    const errs = validateStep(currentStep, formData);
    if (Object.keys(errs)?.length > 0) {
      setStepErrors(errs);
      return;
    }
    setStepErrors({});
    if (currentStep < STEPS?.length) {
      setCurrentStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    setStepErrors({});
    if (currentStep > 1) {
      setCurrentStep((s) => s - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setSubmitError(null);
    try {
      let avatarUrl = null;
      if (formData?.avatarFile && user?.id) {
        const ext = formData?.avatarFile?.name?.split('.')?.pop();
        const path = `professionals/${user?.id}/avatar.${ext}`;
        const { error: uploadErr } = await supabase?.storage?.from('property-assets')?.upload(path, formData?.avatarFile, { upsert: true });
        if (!uploadErr) {
          const { data: urlData } = supabase?.storage?.from('property-assets')?.getPublicUrl(path);
          avatarUrl = urlData?.publicUrl || null;
        }
      }
      const fullName = `${formData?.firstName} ${formData?.lastName}`?.trim();
      const { error: upsertErr } = await supabase?.from('professionals')?.upsert(
        {
          user_id: user?.id,
          full_name: fullName,
          phone: formData?.phone,
          city: formData?.city,
          years_experience: formData?.yearsExp,
          bio: formData?.bio,
          avatar_url: avatarUrl,
          category: (formData?.services || [])?.[0] || null,
          specialty: formData?.specialty,
          availability: formData?.availability,
          commission_rate: formData?.commissionRate,
          onboarding_completed: true,
          updated_at: new Date()?.toISOString(),
        },
        { onConflict: 'user_id' }
      );
      if (upsertErr) throw upsertErr;
      setPhase('success');
    } catch (err) {
      console.error('Onboarding error:', err);
      setSubmitError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setSaving(false);
    }
  };

  if (phase === 'welcome') {
    const name = formData?.firstName || user?.user_metadata?.full_name?.split(' ')?.[0] || '';
    return <WelcomeBanner name={name} onStart={() => setPhase('wizard')} />;
  }

  if (phase === 'success') {
    return <SuccessScreen onGoToDashboard={() => navigate('/professional-dashboard')} />;
  }

  return (
    <>
      <Helmet>
        <title>Configuration du profil professionnel — Kapital Estates</title>
      </Helmet>

      <div className="min-h-screen bg-muted/40">
        {/* Top bar */}
        <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b border-border">
          <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
            <button
              onClick={() => (currentStep === 1 ? setPhase('welcome') : handleBack())}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="ChevronLeft" size={16} />
              <span>Retour</span>
            </button>
            <span className="text-xs text-muted-foreground font-medium">
              Étape {currentStep} sur {STEPS?.length}
            </span>
            <div className="w-16" />
          </div>
          {/* Progress bar */}
          <div className="h-0.5 bg-border">
            <div
              className="h-full bg-primary transition-all duration-500"
              style={{ width: `${(currentStep / STEPS?.length) * 100}%` }}
            />
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-8">
          {/* Stepper */}
          <Stepper currentStep={currentStep} steps={STEPS} />

          {/* Card */}
          <div className="rounded-xl border border-border bg-background shadow-sm p-6 sm:p-8">
            {currentStep === 1 && <StepProfile data={formData} onChange={setFormData} errors={stepErrors} />}
            {currentStep === 2 && <StepServices data={formData} onChange={setFormData} errors={stepErrors} />}
            {currentStep === 3 && <StepAvailability data={formData} onChange={setFormData} errors={stepErrors} />}
            {currentStep === 4 && <StepCommission data={formData} onChange={setFormData} errors={stepErrors} />}
          </div>

          {/* Submit error */}
          {submitError && (
            <div className="mt-4 flex items-center gap-2 px-3 py-2.5 rounded-md border border-destructive/30 bg-destructive/5 text-sm text-destructive">
              <Icon name="AlertCircle" size={14} className="flex-shrink-0" />
              {submitError}
            </div>
          )}

          {/* Navigation */}
          <div className="mt-5 flex items-center gap-3">
            {currentStep > 1 && (
              <Button
                variant="outline"
                size="lg"
                onClick={handleBack}
                className="flex-1"
              >
                Précédent
              </Button>
            )}
            <Button
              size="lg"
              onClick={handleNext}
              loading={saving}
              iconName={currentStep === STEPS?.length ? 'CheckCircle' : 'ArrowRight'}
              iconPosition="right"
              className="flex-1"
            >
              {saving ? 'Enregistrement...' : currentStep === STEPS?.length ? 'Confirmer et terminer' : 'Continuer'}
            </Button>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-4">
            Vous pouvez modifier ces informations à tout moment depuis votre tableau de bord
          </p>
        </div>
      </div>
    </>
  );
};

export default ProfessionalOnboarding;
